/**
 * Login page — both auth paths:
 *   - Password sign-in (fully working now).
 *   - Google / Microsoft SSO (org-scoped; enabled once VITE_ADMIN_ORG_ID is set
 *     and the admin-org provider + admin.<env> DNS are provisioned).
 *
 * On success (or if already authenticated) routes to the originally-intended
 * path, or "/".
 */
import { useState, type FormEvent, type ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth, type SsoProvider } from "../auth/AuthContext";
import { env } from "../config/env";

export function LoginPage(): ReactNode {
  const { identity, loading, loginWithPassword, startSso } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!loading && identity) {
    return <Navigate to={from} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await loginWithPassword(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  }

  async function onSso(provider: SsoProvider) {
    setError(null);
    try {
      await startSso(provider, env.adminOrgId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "SSO is not available yet.");
    }
  }

  const ssoDisabled = !env.adminOrgId;

  return (
    <div className="flex h-screen items-center justify-center bg-slate-50">
      <div className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
        <h1 className="mb-1 text-xl font-bold text-oxbo">OXBO Admin</h1>
        <p className="mb-6 text-sm text-slate-500">super_admin access required</p>

        <div className="space-y-2">
          <button
            type="button"
            onClick={() => void onSso("google")}
            disabled={ssoDisabled}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Continue with Google
          </button>
          <button
            type="button"
            onClick={() => void onSso("microsoft")}
            disabled={ssoDisabled}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Continue with Microsoft
          </button>
          {ssoDisabled && (
            <p className="text-xs text-amber-600">
              SSO pending admin-org configuration (VITE_ADMIN_ORG_ID).
            </p>
          )}
        </div>

        <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
          <span className="h-px flex-1 bg-slate-200" />
          or
          <span className="h-px flex-1 bg-slate-200" />
        </div>

        <form onSubmit={onSubmit} className="space-y-3">
          <input
            type="email"
            required
            placeholder="Email"
            aria-label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
          <input
            type="password"
            required
            placeholder="Password"
            aria-label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded bg-oxbo px-3 py-2 text-sm font-medium text-white hover:bg-oxbo-dark disabled:opacity-50"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
